/* eslint-disable max-len */
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { InspectionType, listings } from '@prisma/client';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormHelperText,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Skeleton,
  IconButton,
  Stack,
  Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import inspectionsService from '../../api/services/inspections';
import { upperCase } from 'lodash';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import inspectionPriceService from '../../api/services/inspection-price';
import { useEffect, useState } from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import useIsMobile from '../../hooks/useIsMobile';
import { Close } from '@mui/icons-material';

// Add interface for inspection price data
interface InspectionPriceItem {
  inspection_type: string;
  price: string | null;
}

interface Props {
  listing: listings;
  onSuccess: () => void;
}

const CreateInspectionModal = NiceModal.create<Props>(
  ({ listing, onSuccess }) => {
    const modal = useModal();
    const isMobile = useIsMobile();
    const [inspectionPrices, setInspectionPrices] = useState<
      InspectionPriceItem[]
    >([]);
    const [totalPriceLoading, setTotalPriceLoading] = useState(false);
    const [totalPrice, setTotalPrice] = useState(0);
    const [priceUnavailable, setPriceUnavailable] = useState(false);

    const formik = useFormik({
      initialValues: {
        type: ['HOME'] as InspectionType[],
      },
      validationSchema: Yup.object({
        type: Yup.array()
          .of(Yup.string())
          .min(1, 'At least one type is required'),
      }),
      onSubmit: async (values) => {
        try {
          await inspectionsService.create({
            type: values.type,
            listing: {
              connect: {
                id: listing.id,
              },
            },
          });
          onSuccess();
          modal.remove();
        } catch (error) {
          console.error(error);
        }
      },
    });
    const inspectionTypes = ['Home', 'Termite', 'Radon', 'Sewer'];

    const handleTypeChange = (type: string) => {
      const upperType = upperCase(type);
      const currentTypes = [...formik.values.type];

      const typeIndex = currentTypes.indexOf(upperType as InspectionType);
      if (typeIndex === -1) {
        currentTypes.push(upperType as InspectionType);
      } else {
        currentTypes.splice(typeIndex, 1);
      }

      formik.setFieldValue('type', currentTypes);
      calculateTotalPrice(currentTypes);
    };

    const fetchInspectionPrice = async () => {
      try {
        setTotalPriceLoading(true);
        setPriceUnavailable(false);
        const prices = await inspectionPriceService.get(listing.id);
        setInspectionPrices(prices);

        // Calculate total price after prices are fetched
        calculateTotalPrice(formik.values.type, prices);
      } catch (error) {
        console.error('Error fetching inspection prices:', error);
        setInspectionPrices([]);
        setTotalPrice(0);
        setPriceUnavailable(true);
      } finally {
        setTotalPriceLoading(false);
      }
    };

    const calculateTotalPrice = (
      selectedTypes: InspectionType[],
      prices = inspectionPrices
    ) => {
      let total = 0;

      // Map inspection types to their API counterparts
      const typeMapping: Record<string, string> = {
        HOME: 'home_inspection',
        TERMITE: 'termite_inspection',
        RADON: 'radon_inspection',
        SEWER: 'sewer_inspection',
      };

      // Add up prices for selected inspection types
      selectedTypes.forEach((type) => {
        const apiType = typeMapping[type];
        if (apiType) {
          const priceItem = prices.find((p) => p.inspection_type === apiType);
          if (priceItem && priceItem.price) {
            total += parseFloat(priceItem.price);
          }
        }
      });

      setTotalPrice(total);
    };

    useEffect(() => {
      fetchInspectionPrice();
    }, []);

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        aria-labelledby="create-inspection-modal"
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            Schedule Inspection
            <IconButton onClick={() => modal.remove()}>
              <Close />
            </IconButton>
          </Stack>
          <Typography variant="body2">
            Homepoint will work with the listing agent to schedule an inspection
            for {listing.address}. You will receive a notification when
            confirmed.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <Grid2 container spacing={2}>
              <Grid2 xs={12}>
                <FormControl
                  fullWidth
                  error={formik.touched.type && Boolean(formik.errors.type)}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Inspection Type
                  </Typography>
                  <FormGroup row>
                    {inspectionTypes.map((type) => (
                      <FormControlLabel
                        key={type}
                        control={
                          <Checkbox
                            checked={formik.values.type.includes(
                              upperCase(type) as InspectionType
                            )}
                            onChange={() => handleTypeChange(type)}
                          />
                        }
                        label={type}
                      />
                    ))}
                  </FormGroup>
                  {formik.touched.type && formik.errors.type && (
                    <FormHelperText>{formik.errors.type}</FormHelperText>
                  )}
                </FormControl>
              </Grid2>
              <Grid2 xs={12}>
                <Typography variant="body1" fontWeight={500} gutterBottom>
                  Inspection Price
                </Typography>
                {totalPriceLoading && (
                  <Skeleton variant="text" width={100} height={24} />
                )}
                {!totalPriceLoading && priceUnavailable && (
                  <Alert severity="info" sx={{ mt: 0.5 }}>
                    Pricing is temporarily unavailable. You can still request
                    the inspection — Homepoint will confirm the price with you
                    before booking.
                  </Alert>
                )}
                {!totalPriceLoading && !priceUnavailable && (
                  <Typography variant="body1">
                    {formatCurrency(totalPrice)}
                  </Typography>
                )}
              </Grid2>
            </Grid2>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => modal.hide()} variant="outlined" color="error">
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }
);

export default CreateInspectionModal;
